import * as React from 'react';
import {
  OVERLAY_MOUNT_EVENT,
  OVERLAY_UNMOUNT_EVENT,
  type OverlayEventDetail,
} from '@spaced-out/ui-design-system';


export type Layers = {
  Panels: Array<OverlayEventDetail>;
  Modals: Array<OverlayEventDetail>;
  Dialogs: Array<OverlayEventDetail>;
};

const INITIAL_LAYERS: Layers = {
  Panels: [],
  Modals: [],
  Dialogs: [],
};

function updateLayers(prev: Layers, detail: unknown, delta: 1 | -1): Layers {
  const eventDetail = detail as OverlayEventDetail | undefined;
  if (!eventDetail) {
    return prev;
  }

  if (eventDetail.overlayType === 'Panel') {
    if (delta === 1) {
      return {...prev, Panels: [...prev.Panels, eventDetail]};
    }
    if (delta === -1) {
      return {
        ...prev,
        Panels: prev.Panels.filter((p) => p.id !== eventDetail.id),
      };
    }
  }
  if (eventDetail.overlayType === 'Modal') {
    if (delta === 1) {
      return {...prev, Modals: [...prev.Modals, eventDetail]};
    }
    if (delta === -1) {
      return {
        ...prev,
        Modals: prev.Modals.filter((m) => m.id !== eventDetail.id),
      };
    }
  }
  if (eventDetail.overlayType === 'Dialog') {
    if (delta === 1) {
      return {...prev, Dialogs: [...prev.Dialogs, eventDetail]};
    }
    if (delta === -1) {
      return {
        ...prev,
        Dialogs: prev.Dialogs.filter((d) => d.id !== eventDetail.id),
      };
    }
  }
  return prev;
}

export const useLayeringContext = (): Layers => {
  const [layers, setLayers] = React.useState<Layers>(INITIAL_LAYERS);

  React.useEffect(() => {
    const onMount = (event: Event) => {
      const customEvent = event as CustomEvent<OverlayEventDetail>;
      const eventDetail = customEvent.detail as OverlayEventDetail;
      if (
        eventDetail.overlayType === 'Panel' ||
        eventDetail.overlayType === 'Dialog' ||
        eventDetail.overlayType === 'Modal'
      ) {
        setLayers((prev) => updateLayers(prev, eventDetail, 1));
      }
    };

    const onUnmount = (event: Event) => {
      const customEvent = event as CustomEvent<OverlayEventDetail>;
      const eventDetail = customEvent.detail as OverlayEventDetail;
      if (
        eventDetail.overlayType === 'Panel' ||
        eventDetail.overlayType === 'Dialog' ||
        eventDetail.overlayType === 'Modal'
      ) {
        setLayers((prev) => updateLayers(prev, eventDetail, -1));
      }
    };

    window.addEventListener(OVERLAY_MOUNT_EVENT, onMount as EventListener);
    window.addEventListener(OVERLAY_UNMOUNT_EVENT, onUnmount as EventListener);

    return () => {
      window.removeEventListener(OVERLAY_MOUNT_EVENT, onMount as EventListener);
      window.removeEventListener(
        OVERLAY_UNMOUNT_EVENT,
        onUnmount as EventListener,
      );
    };
  }, []);

  return layers;
};
